/**
 * المرحلة 1.3 (F05) — «تغيير الميعاد»: the patient moves their own booking.
 *
 * The days and times come from GET /api/bookings/:id/slots — the booking's
 * clinic as the desk sees it, with this booking left out of its own count —
 * and POST /api/bookings/:id/reschedule checks the target again exactly as
 * clinics reception's move is checked. The policy (the booking's
 * institution's patientReschedule) is shown before anything is chosen: how
 * long before the visit, how many times, and what happens to a paid deposit.
 */

import { useEffect, useMemo, useState } from 'react';
import { Button, Icon, Modal, Spinner } from '../../components/common';
import { bookingApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { formatTime12 } from '../../domain/dates';
import { addDays, hospitalClock } from '../../domain/discovery';
import { useI18n } from '../../i18n/I18nProvider';
import { dayLabel } from '../discovery/parts';

const DAYS = 21;
const KNOWN = ['RESCHEDULE_CLOSED', 'RESCHEDULE_TOO_LATE', 'RESCHEDULE_LIMIT', 'SLOT_TAKEN', 'NOT_A_SLOT', 'NOT_OPEN'];

export function RescheduleDialog({ booking, onClose, onMoved }) {
  const { t, lang } = useI18n();
  const { success, error: toastError } = useNotifications();
  const clock = useMemo(() => hospitalClock(), []);
  const days = useMemo(() => Array.from({ length: DAYS }, (_, i) => addDays(clock.today, i)), [clock.today]);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [board, setBoard] = useState({ loading: false, data: null, error: null });
  const [saving, setSaving] = useState(false);
  const policy = booking?.reschedulePolicy || null;

  useEffect(() => {
    if (!booking) return;
    setDate(booking.date >= clock.today ? booking.date : clock.today);
    setTime(null);
  }, [booking, clock.today]);

  const loadBoard = (day) => {
    if (!booking || !day) return undefined;
    let live = true;
    setBoard({ loading: true, data: null, error: null });
    bookingApi
      .slots(booking.id, day)
      .then((data) => live && setBoard({ loading: false, data, error: null }))
      .catch((error) => live && setBoard({ loading: false, data: null, error }));
    return () => {
      live = false;
    };
  };
  useEffect(() => loadBoard(date), [booking, date]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!booking) return null;

  const save = async () => {
    setSaving(true);
    try {
      await bookingApi.reschedule(booking.id, date, time);
      success(t('reschedule.done', { day: dayLabel(date, lang), time: formatTime12(time) }));
      onMoved?.();
    } catch (err) {
      const code = err.body?.code;
      if (code === 'SLOT_TAKEN') {
        setTime(null);
        loadBoard(date);
      }
      toastError(KNOWN.includes(code) ? t(`reschedule.error.${code}`, { hours: policy?.hoursBefore, times: policy?.maxTimes }) : err.message);
    } finally {
      setSaving(false);
    }
  };

  const slots = board.data?.slots || [];
  const same = (s) => date === booking.date && s.time === booking.time;

  return (
    <Modal
      open={Boolean(booking)}
      onClose={onClose}
      title={t('reschedule.title')}
      icon="calendar-clock"
      size="lg"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-slate-400">
            {time ? t('reschedule.to', { day: dayLabel(date, lang), time: formatTime12(time) }) : t('reschedule.pick')}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button icon="check" onClick={save} disabled={!time} loading={saving} data-testid="reschedule-confirm">
              {t('reschedule.confirm')}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4" data-testid="reschedule-dialog">
        <p className="info-note">
          <Icon name="info" size={14} className="mt-0.5 shrink-0" />
          <span>
            {t('reschedule.current', { day: dayLabel(booking.date, lang), time: formatTime12(booking.time) })}{' '}
            {policy && t('reschedule.policy', { hours: policy.hoursBefore, times: policy.maxTimes })}
          </span>
        </p>
        {policy?.deposit === 'forfeit' && Number(booking.depositAmount) > 0 && booking.depositPaid && (
          <p className="reason-note">
            <Icon name="alert-triangle" size={14} className="mt-0.5 shrink-0" />
            <span>{t('reschedule.forfeit')}</span>
          </p>
        )}

        <div className="day-strip" role="group" aria-label={t('bySlot.day')}>
          {days.map((d) => (
            <button
              key={d}
              type="button"
              className={`day-btn ${date === d ? 'is-on' : ''}`}
              onClick={() => {
                setDate(d);
                setTime(null);
              }}
              aria-pressed={date === d}
              data-testid={`reschedule-day-${d}`}
            >
              <span>{d === clock.today ? t('bySlot.today') : dayLabel(d, lang, { weekday: 'short' })}</span>
              <strong>{Number(d.slice(8))}</strong>
              <span>{dayLabel(d, lang, { month: 'short' })}</span>
            </button>
          ))}
        </div>

        {board.loading ? (
          <Spinner variant="inline" label={t('reschedule.loading')} />
        ) : board.error ? (
          <p className="reason-note">{board.error.message}</p>
        ) : board.data?.closed ? (
          <p className="text-sm text-slate-400">{board.data.reason || t('reschedule.closedDay')}</p>
        ) : slots.length === 0 ? (
          <p className="text-sm text-slate-400">{t('reschedule.closedDay')}</p>
        ) : (
          <div className="slot-grid">
            {slots.map((s) => {
              const off = s.past || s.free <= 0 || same(s);
              return (
                <button
                  key={s.time}
                  type="button"
                  className={`slot-btn ${time === s.time ? 'is-on' : ''}`}
                  disabled={off}
                  onClick={() => setTime(s.time)}
                  title={same(s) ? t('reschedule.current', { day: '', time: formatTime12(s.time) }) : s.free <= 0 ? t('reschedule.full') : undefined}
                  data-testid="reschedule-slot"
                  data-time={s.time}
                >
                  {formatTime12(s.time)}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default RescheduleDialog;
